
import React, { useEffect, useRef, useState } from 'react';
import { Alert, AlertDescription } from './ui/alert';
import { AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { useIsMobile } from '@/hooks/use-mobile';
import MobileInstructions from './games/common/MobileInstructions';

interface MatchingGameProps {
  title: string;
}

const MatchingGame = ({ title }: MatchingGameProps) => {
  const gameContainerRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [score, setScore] = useState(0);
  const [matches, setMatches] = useState(0);
  const [flippedCards, setFlippedCards] = useState<number[]>([]);
  const [matchedCards, setMatchedCards] = useState<number[]>([]);
  const isMobile = useIsMobile();
  
  // Card symbols
  const symbols = ['🌞', '🌙', '🌸', '🍀', '🌈', '🪷', '🦋', '🌎'];
  const [cards, setCards] = useState<string[]>([]);
  
  useEffect(() => {
    try {
      // Initialize the game
      initGame();
    } catch (error) {
      console.error("Failed to initialize Matching game:", error);
      setError("Failed to initialize the Matching Exercise. Please try refreshing the page.");
      setIsLoading(false);
    }
  }, []);
  
  const initGame = () => {
    // Create cards - two of each symbol
    const allCards = [...symbols, ...symbols];
    
    // Shuffle cards
    for (let i = allCards.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [allCards[i], allCards[j]] = [allCards[j], allCards[i]];
    }
    
    setCards(allCards);
    setFlippedCards([]);
    setMatchedCards([]);
    setScore(0);
    setMatches(0);
    setIsLoading(false);
  };
  
  const handleCardClick = (index: number) => {
    // Ignore if card is already flipped or matched
    if (flippedCards.includes(index) || matchedCards.includes(index)) {
      return;
    }
    
    // Limit to 2 cards flipped at once
    if (flippedCards.length === 2) {
      return;
    }
    
    // Add card to flipped cards
    const newFlippedCards = [...flippedCards, index];
    setFlippedCards(newFlippedCards);
    
    // Check for match if 2 cards are flipped
    if (newFlippedCards.length === 2) {
      const [first, second] = newFlippedCards;
      
      if (cards[first] === cards[second]) {
        // Match found!
        setMatchedCards([...matchedCards, first, second]);
        setFlippedCards([]);
        setMatches(matches + 1);
        setScore(score + 10);
      } else {
        // No match, flip back after delay
        setTimeout(() => {
          setFlippedCards([]);
        }, 1000);
      }
    }
  };
  
  const handleRetry = () => {
    setIsLoading(true);
    setError(null);
    initGame();
  };
  
  // If there's an error, show an alert
  if (error) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center p-6">
        <Alert variant="destructive" className="max-w-md mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
        <Button onClick={handleRetry} className="mt-4">Retry</Button>
      </div>
    );
  }
  
  // Check for game completion
  if (matches === symbols.length && cards.length > 0) {
    return (
      <div 
        className="w-full h-full flex flex-col items-center justify-center p-4 bg-gradient-to-b from-green-50 to-green-100"
        role="alert"
        aria-live="polite"
      >
        <div className="text-center">
          <h2 className={`${isMobile ? 'text-xl' : 'text-2xl'} font-bold mb-4`}>🎉 Congratulations! 🎉</h2>
          <p className={`${isMobile ? 'text-base' : 'text-lg'} mb-6`}>You found all the matches!</p>
          <p className={`${isMobile ? 'text-lg' : 'text-xl'} font-bold mb-6`}>Your score: {score}</p>
          <Button onClick={handleRetry}>Play Again</Button>
        </div>
      </div>
    );
  }

  // Calculate proper card size based on device
  const cardSizeClass = isMobile 
    ? "w-12 h-12 text-xl" 
    : "w-16 h-16 sm:w-24 sm:h-24 text-3xl";
    
  // Use different columns for different screen sizes
  const gridClass = isMobile 
    ? (window.innerWidth < 360 ? "grid-cols-2" : "grid-cols-3") 
    : "grid-cols-4";

  return (
    <div 
      className="w-full h-full flex flex-col items-center justify-center relative p-2 sm:p-4"
      role="application"
      aria-label="Matching Game"
    >
      {isLoading && (
        <div 
          className="absolute inset-0 bg-white bg-opacity-80 flex items-center justify-center z-10"
          role="alert"
          aria-live="polite"
          aria-busy="true"
        >
          <div className="text-center">
            <div 
              className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-coral border-r-transparent mb-4"
              aria-hidden="true"
            ></div>
            <p id="loading-status">Loading Matching Game...</p>
          </div>
        </div>
      )}
      
      <div 
        className="w-full text-center mb-2 sm:mb-4"
        aria-live="polite"
      >
        <h2 
          className={`${isMobile ? 'text-lg' : 'text-2xl'} font-bold mb-1`}
          id="game-title"
        >Match & Meditate</h2>
        <p className={`${isMobile ? 'text-xs' : 'text-base'} mb-1 sm:mb-2`}>Find matching pairs while practicing mindful focus</p>
        <p className="font-bold mb-2 sm:mb-4" aria-live="polite">Score: {score}</p>
      </div>
      
      <div 
        className={`grid ${gridClass} gap-2 m-auto max-w-xs sm:max-w-md`}
        role="grid"
        aria-label="Memory card grid"
      >
        {cards.map((symbol, index) => (
          <div 
            key={index}
            className={`${cardSizeClass} flex items-center justify-center rounded-lg cursor-pointer shadow-sm
                      ${flippedCards.includes(index) || matchedCards.includes(index) 
                        ? 'bg-white border-2 border-green-500' 
                        : 'bg-blue-500'} 
                      transition-all duration-200 hover:shadow`}
            onClick={() => handleCardClick(index)}
            role="button"
            aria-label={flippedCards.includes(index) || matchedCards.includes(index) 
              ? `Card ${index + 1}: ${symbol}` 
              : `Card ${index + 1}: Hidden`}
            aria-pressed={flippedCards.includes(index) || matchedCards.includes(index)}
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleCardClick(index);
              }
            }}
          >
            {(flippedCards.includes(index) || matchedCards.includes(index)) ? symbol : ''}
          </div>
        ))}
      </div>
      
      <div 
        className="mt-3 sm:mt-6 px-2 max-w-xs sm:max-w-md"
        role="region"
        aria-label="Game instructions"
      >
        <p className="text-center text-xs sm:text-sm text-gray-600">
          Take deep breaths as you search for matches. <br />
          Focus on being present with each card you flip.
        </p>
      </div>

      {isMobile && (
        <MobileInstructions gameType="Matching" />
      )}
    </div>
  );
};

export default MatchingGame;

